/** @format */

import { Disclosure } from "@headlessui/react";
import type React from "react";
import { useEffect, useRef, useState } from "react";

/** @format */

import type { ReactNode } from "react";
import { isHttpError, isNetworkError, isTimeoutError } from "@/api/galaxieClient";
import Crash from "../assets/crash_low.webp";
import { LOADING_MESSAGES } from "./constants";
import LoadingCircle from "./LoadingCircle";

interface LoadingProps {
  statuses?: string[];
  errors?: (Error | null | undefined)[];
  children?: ReactNode;
  wrap?: boolean;
  className?: string;
  fontClassName?: string;
}

const Loading: React.FC<LoadingProps> = ({
  statuses = ["loading"],
  errors = [],
  children = null,
  wrap = false,
  className = "flex flex-col justify-center grow items-center content-center m-auto mt-4",
  ...props
}) => {
  // status can be "idle", "loading", "success", "error"
  // if all statuses are "success", then status is "success"
  // if any status is "error", then status is "error"
  // if any status is "loading", then status is "loading"
  // if all statuses are "idle", then status is "idle"
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  useEffect(() => {
    setTimeout(() => {
      setCurrentMessageIndex(Math.floor(Math.random() * LOADING_MESSAGES.length));
    }, 0);
    const interval = setInterval(() => {
      setCurrentMessageIndex(Math.floor(Math.random() * LOADING_MESSAGES.length));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const loadingMessage = LOADING_MESSAGES[currentMessageIndex];

  const status = statuses.reduce((acc: string | null, status) => {
    if (acc === null) {
      return status;
    }
    if (acc === "error") {
      return acc;
    }
    if (status === "error") {
      return "error";
    }
    if (acc === "loading") {
      return acc;
    }
    if (status === "loading") {
      return "loading";
    }
    if (acc === "idle") {
      return acc;
    }
    if (status === "idle") {
      return "idle";
    }
    if (acc === "pending") {
      return acc;
    }
    if (status === "pending") {
      return "loading";
    }
    return "success";
  }, null);

  // Once all queries have succeeded, keep showing children through transient
  // refetch errors (stale cached data is still valid). Resets when any query
  // goes back to pending/loading (e.g., runID change triggers a fresh fetch).
  const hadSuccess = useRef(false);
  if (status === "pending" || status === "loading") {
    hadSuccess.current = false;
  }
  if (status === "success") {
    hadSuccess.current = true;
  }
  const effectiveStatus = status === "error" && hadSuccess.current ? "success" : status;

  switch (effectiveStatus) {
    case "success":
      return wrap ? (
        <div {...props} className={className}>
          {children}
        </div>
      ) : (
        children
      );
    case "error":
      return (
        <div {...props} className={className}>
          <div className="flex flex-col bg-slate-900 border border-slate-700 rounded-3xl justify-center align-middle shrink my-auto p-4">
            <h2 className="text-center text-3xl font-bold my-3 text-red-400 shrink">
              Something went wrong
            </h2>
            <Disclosure as="div">
              {({ open }) => (
                <div className="text-center">
                  <Disclosure.Button className="text-red-400 border border-red-400/50 rounded-md px-2 py-1 hover:bg-red-400/10">
                    {open ? "Hide" : "Show"} Details
                  </Disclosure.Button>
                  <Disclosure.Panel>
                    <pre className="text-center shrink text-slate-300">
                      {errors
                        .filter(error => error)
                        .map((error: Error | null | undefined) => {
                          // Handle new error types
                          let errorMessage = "";
                          let errorUrl = "Unknown URL";
                          let hrefUrl = "#";

                          if (isNetworkError(error)) {
                            errorMessage =
                              error.message ||
                              "Network error: Unable to connect to server";
                          } else if (isTimeoutError(error)) {
                            errorMessage =
                              error.message ||
                              `Request timed out after ${error.timeout}ms`;
                          } else if (isHttpError(error)) {
                            errorMessage =
                              error.statusText || `HTTP ${error.status} error`;
                            // Try to extract URL from error data if available
                            const errorData = error.data as
                              | { url?: string }
                              | undefined;
                            if (errorData?.url) {
                              errorUrl = errorData.url;
                              hrefUrl = errorData.url;
                            }
                          } else {
                            // Fallback to existing error handling
                            const url = (error as { config?: { url?: string } })?.config
                              ?.url;
                            errorMessage =
                              (error as { message?: string })?.message ??
                              "Unknown error";
                            errorUrl = url
                              ? url.startsWith("/api")
                                ? url
                                : `/api${url}`
                              : "Unknown URL";
                            hrefUrl = url
                              ? url.startsWith("/api")
                                ? url
                                : `/api${url}`
                              : "#";
                          }

                          const displayUrl = errorUrl;

                          return (
                            <div key={`${errorMessage}-${displayUrl}`}>
                              {displayUrl !== "Unknown URL" && (
                                <a
                                  className="text-blue-400"
                                  target="_blank"
                                  rel="noreferrer"
                                  href={hrefUrl}
                                >
                                  {displayUrl}
                                </a>
                              )}
                              {displayUrl !== "Unknown URL" && " : "}
                              {errorMessage}
                            </div>
                          );
                        })}
                    </pre>
                  </Disclosure.Panel>
                </div>
              )}
            </Disclosure>
            <img
              src={Crash}
              alt="error"
              className="object-contain w-1/2 m-auto"
              width="1024"
              height="817"
            />
          </div>
        </div>
      );
    default:
      return (
        <div {...props} className={className}>
          <div className="flex flex-col grow justify-center items-center h-full w-full">
            {status === "pending" || status === "loading" ? (
              <div className="flex flex-col justify-center items-center">
                <LoadingCircle className="h-auto w-full max-h-[100px]" />
                <h2 className="text-center text-2xl capitalize">{loadingMessage}</h2>
              </div>
            ) : (
              <h2 className="text-center text-4xl capitalize">status</h2>
            )}
          </div>
        </div>
      );
  }
};

export default Loading;
